<template>
    <PageLoading :loading="loading">
        <!-- 错误页面 -->
        <ErrorPage v-if="hasError" :title="errorTitle" :message="errorMessage" />

        <!-- 正常页面 -->
        <div v-else class="balance-page">
            <!-- 余额卡片 -->
            <div class="balance-card">
                <div class="balance-label">当前余额</div>
                <div class="balance-amount">¥{{ balance }}</div>
                <van-button class="recharge-btn" type="primary" @click="handleRecharge">立即充值</van-button>
            </div>

            <!-- 余额明细 -->
            <div class="transaction-section">
                <div class="section-title">余额明细</div>
                <!-- 空状态 -->
                <div v-if="transactions.length === 0" class="empty-state">
                    <van-empty description="暂无交易记录" />
                </div>
                <!-- 交易记录列表 -->
                <div v-else>
                    <div class="transaction-list">
                        <div class="transaction-item" v-for="transaction in transactions" :key="transaction.id">
                            <div class="transaction-info">
                                <div class="transaction-desc">{{ transaction.source }}</div>
                                <div class="transaction-time">{{ transaction.use_time }}</div>
                            </div>
                            <div class="transaction-amount" :class="transaction.amount > 0 ? 'credit' : 'debit'">
                                {{ transaction.amount > 0 ? '+' : '' }}{{ transaction.amount }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </PageLoading>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { setWxTitle } from '@/utils/wx'
import { post } from '@/utils/request'
import { WECHAT_APPID } from '../../../config/env'

// 加载状态
const loading = ref(true)

// 错误状态
const hasError = ref(false)
const errorTitle = ref('')
const errorMessage = ref('')

// 当前余额
const balance = ref('0.00')

// 交易记录
const transactions = ref([])

// 处理错误
const handleError = (title, message) => {
    hasError.value = true
    errorTitle.value = title
    errorMessage.value = message
    loading.value = false
}

onMounted(() => {
    setWxTitle('我的余额')
    initPage()
})

// 初始化微信授权
const initWechatAuth = () => {
    // 获取当前url中的code参数
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')

    // 判断用户取消授权
    const error = urlParams.get('error')
    const errorDesc = urlParams.get('error_description')
    if (error) {
        console.log('用户取消授权:', errorDesc)
        handleError('获取用户信息失败', errorDesc)
        return
    }

    if (!code) {
        // 如果没有code，重定向到微信授权页面
        const redirectUri = encodeURIComponent(window.location.href)
        const appId = WECHAT_APPID
        window.location.href = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_userinfo&state=STATE#wechat_redirect`
    } else {
        // 有code则获取用户信息
        getUserInfo({
            code: code,
            app_id: WECHAT_APPID,
            page_type: '首页'
        })
    }
}

// 获取用户信息
const getUserInfo = async (data) => {
    try {
        const res = await post('/api/index/info', data)

        if (res.result && res.result.state === 'ok') {
            const userInfo = res.result.partner_info || {}
            const accountInfo = res.result.account_info || {}
            // 存储用户ID到本地存储
            if (userInfo.id) {
                localStorage.setItem('userId', userInfo.id)
            }
            if (accountInfo.company_ids) {
                localStorage.setItem('company_ids', JSON.stringify(accountInfo.company_ids))
            }
            if (accountInfo.user_default_image) {
                localStorage.setItem('user_default_image', accountInfo.user_default_image)
            }
            initPage()
        } else {
            handleError('获取用户信息失败', res.result?.message || '获取用户信息失败')
        }
    } catch (error) {
        console.error('获取用户信息失败:', error)
        handleError('获取用户信息失败', '获取用户信息失败，请重试')
    } finally {
        loading.value = false
    }
}

// 页面初始化
const initPage = async () => {
    try {
        // 检查是否已有用户ID
        if (localStorage.getItem('userId')) {
            // 这里可以调用接口获取余额和交易记录数据
            const res = await post('/api/select/community/recharge/record', {
                partner_id: parseInt(localStorage.getItem('userId')),
                company_ids: JSON.parse(localStorage.getItem('company_ids')),
            })
            if (res.result && res.result.state === 'ok') {
                balance.value = res.result.stored_account
                transactions.value = res.result.recharge_record_arr
            } else {
                handleError('加载失败', res.result?.message || '接口异常')
            }
            loading.value = false
        } else {
            initWechatAuth()
        }
    } catch (error) {
        console.error('页面初始化失败:', error)
        handleError('加载失败', '页面加载失败，请重试')
        loading.value = false
    }
}

// 充值处理
const handleRecharge = () => {
    // 跳转到充值页面
    window.location.href = '/recharge'
}

</script>

<style scoped>
.balance-page {
    min-height: calc(100vh - 32px);
    background: #fff;
    padding: 16px;
}

.balance-card {
    background: var(--van-primary-color);
    border-radius: 12px;
    padding: 24px;
    color: #fff;
}

.balance-label {
    font-size: 14px;
    margin-bottom: 8px;
    opacity: 0.9;
}

.balance-amount {
    font-size: 30px;
    font-weight: 500;
    margin-bottom: 16px;
}

.recharge-btn {
    background: #fff;
    color: var(--van-primary-color);
    padding: 6px 22px;
    font-size: 14px;
    font-weight: 500;
    border-radius: 9999px;
    height: 36px;
}

.section-title {
    padding: 24px 0 16px 0;
    font-size: 18px;
    font-weight: 500;
    color: #000;
}

.transaction-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.empty-state {
    padding: 60px 0;
    text-align: center;
}

.transaction-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-radius: 8px;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
}

.transaction-info {
    flex: 1;
}

.transaction-desc {
    font-size: 16px;
    color: #000000;
    margin-bottom: 6px;
    font-weight: 500;
}

.transaction-time {
    font-size: 14px;
    color: #6B7280;
}

.transaction-amount {
    font-size: 16px;
    font-weight: 600;
}

.transaction-amount.credit {
    color: #16A34A;
}

.transaction-amount.debit {
    color: var(--van-primary-color);
}
</style>